"use client";

import React from "react";
import Link from "next/link";

const PRIVACY_POLICY_VERSION = "1.0";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Privacy Policy for Mira Fact Verifier
          </h1>
          
          <div className="text-gray-600 mb-6">
            Version: {PRIVACY_POLICY_VERSION}
          </div>

          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p className="mb-4">
                Mira Fact Verifier (&quot;Mira&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our fact-checking service, 
                including our web application and Chrome extension.
              </p>
              <p className="mb-4">
                By using Mira, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Information We Collect
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-4">
                2.1 Information You Provide to Us
              </h3>
              
              <h4 className="font-medium text-gray-800 mb-2">Account Information</h4>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li><strong>Email address</strong> - Required for account registration and authentication</li>
                <li><strong>Profile information</strong> - Optional information you may add to your profile</li>
              </ul>

              <h4 className="font-medium text-gray-800 mb-2">Verification Data</h4>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li><strong>Selected text</strong> - The specific text you choose to verify</li>
                <li><strong>Source URL</strong> - The webpage where you found the text (when using the extension)</li>
                <li><strong>Verification preferences</strong> - Your chosen consensus requirements (2 of 3 or 3 of 3 models)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-4">
                2.2 Usage Analytics
              </h3>
              <p className="mb-2">We track the following metrics to improve our service and provide you with insights:</p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li><strong>Verification activity</strong> - How many verifications you perform</li>
                <li><strong>Accuracy statistics</strong> - Your verification results breakdown</li>
                <li><strong>Performance metrics</strong> - Service response times</li>
                <li><strong>Source domains</strong> - Websites where your verifications originated</li>
                <li><strong>Feature usage</strong> - Which features you use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. How We Use Your Information
              </h2>
              <p className="mb-4">We use the collected information for the following purposes:</p>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.1 Service Delivery</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Process your fact verification requests</li>
                <li>Maintain your verification history</li>
                <li>Enforce rate limits and prevent abuse</li>
                <li>Provide personalized insights and analytics</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.2 Account Management</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Authenticate your identity</li>
                <li>Manage your account and preferences</li>
                <li>Communicate important service updates</li>
                <li>Respond to your support requests</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.3 Service Improvement</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Analyze usage patterns to improve features</li>
                <li>Debug technical issues and errors</li>
                <li>Develop new functionality</li>
                <li>Optimize performance and reliability</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.4 Legal and Security</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Comply with legal obligations</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Protect our rights and property</li>
                <li>Ensure service security and integrity</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. Third-Party Services
              </h2>
              <p className="mb-4">We use the following third-party services to provide and improve Mira:</p>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">4.1 AI Verification Providers</h3>
              <p className="mb-2">Your verification texts are sent to these services for fact-checking:</p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li><strong>OpenAI</strong> (GPT models)</li>
                <li><strong>Anthropic</strong> (Claude models)</li>
                <li><strong>Google</strong> (Gemini models)</li>
              </ul>
              <p className="mb-4">These providers process your text solely for verification purposes according to their respective privacy policies.</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">4.2 Service Providers</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li><strong>Supabase</strong> - Authentication and user management</li>
                <li><strong>Cloudflare</strong> - Content delivery and security</li>
                <li><strong>Google Analytics</strong> - Anonymous usage analytics</li>
                <li><strong>Sentry</strong> - Error tracking and monitoring</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Data Storage and Retention
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.1 Data Retention</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li><strong>Account information</strong> - Until you delete your account</li>
                <li><strong>Verification history</strong> - Retained until you request deletion</li>
                <li><strong>Usage analytics</strong> - Aggregated anonymously for service improvement</li>
                <li><strong>Session data</strong> - Expires when you log out or after inactivity</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.2 Browser Storage (Extension)</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Session tokens for authentication</li>
                <li>User preferences (auto-highlight, notifications)</li>
                <li>Temporary verification cache</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Data Sharing and Disclosure
              </h2>
              <p className="mb-4">We do not sell, trade, or rent your personal information. We may share your information only in the following circumstances:</p>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">6.1 With Your Consent</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>When you explicitly agree to share information</li>
                <li>When you choose to make verifications public (future feature)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">6.2 Service Providers</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Third-party services that help us operate Mira</li>
                <li>Only shared to the extent necessary for service delivery</li>
                <li>Bound by confidentiality agreements</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">6.3 Legal Requirements</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>To comply with legal obligations</li>
                <li>To respond to lawful requests by public authorities</li>
                <li>To protect our rights, privacy, safety, or property</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Data Security
              </h2>
              <p className="mb-4">We implement appropriate technical and organizational measures to protect your information:</p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li><strong>Encryption</strong> - All data transmitted using HTTPS/TLS</li>
                <li><strong>Authentication</strong> - Secure authentication via Supabase</li>
                <li><strong>Access controls</strong> - Your data is isolated and only accessible to you</li>
                <li><strong>Monitoring</strong> - Continuous security monitoring and alerts</li>
              </ul>
              <p className="mb-4">However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Your Rights and Choices
              </h2>
              <p className="mb-4">You have the following rights regarding your personal information:</p>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">8.1 Access and Portability</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Request a copy of your personal data</li>
                <li>Export your verification history in CSV or JSON format</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">8.2 Correction</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Update your account information</li>
                <li>Correct any inaccurate data</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">8.3 Deletion</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Delete specific verifications</li>
                <li>Delete your entire account and all associated data</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">8.4 Communication Preferences</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Manage notification settings in the extension</li>
              </ul>

              <p className="mt-4">
                To exercise these rights, contact us at{" "}
                <a 
                  href="mailto:verify-privacy@mira.network" 
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  verify-privacy@mira.network
                </a>
                .
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Children&apos;s Privacy
              </h2>
              <p className="mb-4">
                Mira is not intended for use by children under 13 years of age. We do not knowingly collect personal information from children under 13. 
                If we discover that a child under 13 has provided us with personal information, we will delete it immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. International Data Transfers
              </h2>
              <p className="mb-4">
                Your information may be transferred to and processed in countries other than your country of residence. 
                These countries may have data protection laws different from your country. We ensure appropriate safeguards are in place to protect your information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. Chrome Extension Specific Practices
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">11.1 Permissions</h3>
              <p className="mb-2">The Mira Chrome extension requires the following permissions:</p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li><strong>activeTab</strong> - To read selected text on the current page</li>
                <li><strong>contextMenus</strong> - To add &quot;Verify with Mira&quot; option</li>
                <li><strong>storage</strong> - To save your preferences and session</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">11.2 Data Collection</h3>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Only collects text you explicitly select for verification</li>
                <li>Does not track browsing history</li>
                <li>Does not modify webpage content</li>
                <li>Does not collect data from other tabs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                12. Updates to This Policy
              </h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page. 
                Your continued use of Mira after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                13. Contact Us
              </h2>
              <p className="mb-4">
                If you have questions or concerns about this Privacy Policy or our practices, please contact us:
              </p>
              <p className="mb-2">
                <strong>Email:</strong>{" "}
                <a 
                  href="mailto:verify-privacy@mira.network" 
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  verify-privacy@mira.network
                </a>
              </p>
              <p>
                <strong>Website:</strong>{" "}
                <a 
                  href="https://verify.mira.network" 
                  className="text-blue-600 hover:text-blue-800 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://verify.mira.network
                </a>
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <Link 
              href="/" 
              className="text-blue-600 hover:text-blue-800 underline"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}